import Alpine from "alpinejs";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(MorphSVGPlugin);
gsap.registerPlugin(CustomEase);

const HOME_JOURNAL_MAIN_IMAGE_CLIP_PATHS = [
  "M462 160.689C462 277.049 426.533 338 231 338C79.4062 338 8.4 304.014 0 160.689C0 62.7621 42.4667 0 231 0C358.578 0 462 48.4187 462 160.689Z",
  "M462 166C456 272 420 334 236 338C82 342 11 301 3 164C-3 69 48 6 229 4C362 2 466 53 462 166Z",
  "M462 155C466 281 431 336 225 333C76 331 4 309 0 156C-2 59 53 3 238 0C354 -2 459 43 462 155Z",
];

const HOME_JOURNAL_SUB_IMAGE_CLIP_PATHS = [
  "M276 112.918C276 197.628 255.62 242 168.861 242C58.0459 242 6.14039 217.258 0 112.918C0 41.6282 54.7342 0 132.759 0C192.733 5.22302 276 31.1863 276 112.918Z",
  "M276 118C272 195 251 239 170 242C61 245 8 216 2 117C-2 47 57 5 134 3C198 5 279 36 276 118Z",
  "M276 108C280 201 258 240 164 238C56 236 4 222 0 109C-1 38 62 2 137 0C190 3 273 26 276 108Z",
];
const HOME_JOURNAL_IMAGE_POP_EASE = CustomEase.create(
  "homeJournalImagePopEase",
  "M0,0 C0.12,0.72 0.22,1.18 0.42,1.08 C0.54,1.01 0.6,0.965 0.7,0.988 C0.8,1.012 0.9,1.004 1,1"
);

const getExistingElements = (
  elements: Array<Element | null | undefined>
): Element[] =>
  elements.filter((element): element is Element => element instanceof Element);

const splitTextToChars = (element: HTMLElement) => {
  if (element.dataset.homeJournalAnimationSplit === "true") {
    return Array.from(
      element.querySelectorAll<HTMLElement>("[data-home-journal-animation-char]")
    );
  }

  const chars: HTMLElement[] = [];
  const lines: HTMLElement[] = [];
  let currentLine = document.createElement("span");
  let currentLineInner = document.createElement("span");

  const resetCurrentLine = () => {
    currentLine = document.createElement("span");
    currentLine.className = "home-journal-animation__line";
    currentLine.style.display = "block";
    currentLine.style.overflow = "hidden";

    currentLineInner = document.createElement("span");
    currentLineInner.className = "home-journal-animation__line-inner";
    currentLineInner.style.display = "inline-block";
    currentLine.appendChild(currentLineInner);
  };

  const pushCurrentLine = () => {
    if (currentLineInner.childNodes.length === 0) {
      return;
    }

    lines.push(currentLine);
    resetCurrentLine();
  };

  resetCurrentLine();

  const splitNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || "").replace(/[\t\r\n]+/g, "").trim();

      if (!text) {
        return;
      }

      Array.from(text).forEach((char) => {
        const charElement = document.createElement("span");
        charElement.className = "home-journal-animation__char";
        charElement.dataset.homeJournalAnimationChar = "true";
        charElement.style.display = "inline-block";
        charElement.textContent = char === " " ? "\u00A0" : char;
        currentLineInner.appendChild(charElement);
        chars.push(charElement);
      });

      return;
    }

    if (node instanceof HTMLBRElement) {
      pushCurrentLine();
      return;
    }

    Array.from(node.childNodes).forEach(splitNode);
  };

  Array.from(element.childNodes).forEach(splitNode);
  pushCurrentLine();
  element.replaceChildren(...lines);
  element.dataset.homeJournalAnimationSplit = "true";

  return chars;
};

const addImageAnimation = ({
  timeline,
  image,
  clip,
  innerImage,
  clipPaths,
  label,
  position,
}: {
  timeline: gsap.core.Timeline;
  image: Element;
  clip: SVGPathElement;
  innerImage: SVGImageElement;
  clipPaths: string[];
  label: string;
  position: string;
}) => {
  timeline
    .addLabel(label, position)
    .to(
      image,
      {
        autoAlpha: 1,
        duration: 0.01,
        ease: "none",
      },
      label
    )
    .to(
      image,
      {
        scale: 1,
        duration: 1.65,
        ease: HOME_JOURNAL_IMAGE_POP_EASE,
        onComplete: () => {
          gsap.set(image, { willChange: "auto" });
        },
      },
      label
    )
    .to(
      clip,
      {
        duration: 1.8,
        ease: "sine.inOut",
        morphSVG: {
          shape: clipPaths[0],
          shapeIndex: 0,
        },
      },
      `${label}+=0.25`
    )
    .to(
      innerImage,
      {
        scale: 1,
        duration: 2.3,
        ease: HOME_JOURNAL_IMAGE_POP_EASE,
      },
      `${label}+=0.1`
    );
};

export const initHomeJournalAnimation = () => {
  Alpine.data("homeJournalAnimation", () => {
    return {
      init() {
        const root = this.$el as HTMLElement;
        const title = this.$refs.title as HTMLElement | undefined;
        const mainImage = this.$refs.mainImage as Element | undefined;
        const mainImageClip = this.$refs.mainImageClip as unknown as
          | SVGPathElement
          | undefined;
        const mainImageInner = this.$refs.mainImageInner as unknown as
          | SVGImageElement
          | undefined;
        const subImage = this.$refs.subImage as Element | undefined;
        const subImageClip = this.$refs.subImageClip as unknown as
          | SVGPathElement
          | undefined;
        const subImageInner = this.$refs.subImageInner as unknown as
          | SVGImageElement
          | undefined;
        const imageElements = getExistingElements([mainImage, subImage]);
        const innerImageElements = getExistingElements([
          mainImageInner,
          subImageInner,
        ]);
        const titleChars = title ? splitTextToChars(title) : [];
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        if (titleChars.length === 0 && imageElements.length === 0) {
          return;
        }

        gsap.set(titleChars, {
          opacity: 0,
          xPercent: -80,
          transformOrigin: "0% 50%",
          willChange: "transform, opacity",
        });
        gsap.set(imageElements, {
          autoAlpha: 0,
          scale: 0.82,
          transformOrigin: "50% 50%",
          force3D: true,
          willChange: "transform, opacity",
        });
        gsap.set(innerImageElements, {
          scale: 1.18,
          transformOrigin: "center center",
          transformBox: "fill-box",
        });
        if (mainImageClip) {
          gsap.set(mainImageClip, {
            attr: { d: HOME_JOURNAL_MAIN_IMAGE_CLIP_PATHS[2] },
          });
        }
        if (subImageClip) {
          gsap.set(subImageClip, {
            attr: { d: HOME_JOURNAL_SUB_IMAGE_CLIP_PATHS[2] },
          });
        }

        if (prefersReducedMotion) {
          gsap.set(titleChars, {
            opacity: 1,
            xPercent: 0,
            willChange: "auto",
          });
          gsap.set(imageElements, {
            autoAlpha: 1,
            scale: 1,
            willChange: "auto",
          });
          gsap.set(innerImageElements, {
            scale: 1,
          });
          return;
        }

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: root,
            start: "top 75%",
            once: true,
          },
        });

        timeline.to(titleChars, {
            opacity: 1,
            xPercent: 0,
            duration: 0.7,
            ease: "back.out(1.45)",
            stagger: 0.04,
            onComplete: () => {
              gsap.set(titleChars, { willChange: "auto" });
            },
          });

        if (mainImage && mainImageClip && mainImageInner) {
          addImageAnimation({
            timeline,
            image: mainImage,
            clip: mainImageClip,
            innerImage: mainImageInner,
            clipPaths: HOME_JOURNAL_MAIN_IMAGE_CLIP_PATHS,
            label: "mainImageIn",
            position: "0.25",
          });
        }

        if (subImage && subImageClip && subImageInner) {
          addImageAnimation({
            timeline,
            image: subImage,
            clip: subImageClip,
            innerImage: subImageInner,
            clipPaths: HOME_JOURNAL_SUB_IMAGE_CLIP_PATHS,
            label: "subImageIn",
            position: "0.37",
          });
        }
      },
    };
  });
};
